import React from 'react';
import { Alert, Button, Col, Space, Spin, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { epcContractAdminService } from '@/services/epcContractAdmin.service';

const { Title, Paragraph, Text } = Typography;

function fmtMoney(currency: string, v: number | undefined) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${currency} ${Number(v).toLocaleString()}`;
}

const EpcContractAdminControlPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const towerQ = useQuery({
    queryKey: ['epc-contract-admin-control-tower', id],
    queryFn: () => epcContractAdminService.getControlTower(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  const forecastQ = useQuery({
    queryKey: ['epc-contract-admin-forecast', id, 'control'],
    queryFn: () => epcContractAdminService.getForecast(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  if (!id) return null;

  if (towerQ.isLoading && forecastQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (towerQ.isError && forecastQ.isError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message={
            (towerQ.error as Error)?.message ||
            (forecastQ.error as Error)?.message ||
            'Failed to load control tower'
          }
        />
      </div>
    );
  }

  const forecast = towerQ.data?.forecast || forecastQ.data;
  const counts = towerQ.data?.counts;
  const currency = String(forecast?.currency || towerQ.data?.currency || 'AED');

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/epc/commercial/contracts/${id}`)}
        >
          Contract workspace
        </Button>
      </Space>
      <Title level={3} style={{ marginBottom: 4 }}>
        Contract commercial control
      </Title>
      <Paragraph type="secondary">
        Advisory commercial exposure for this contract (no GL). Certification remains the bridge to
        draft Tax Invoice.
      </Paragraph>

      {forecast ? (
        <ControlTowerMetricStrip zone="attention" title="Commercial forecast">
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Contract value"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.contractValue)}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Expected approved"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.expectedApprovedValue)}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Certified to date (net)"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.certifiedToDate)}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Claims open"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.claimsOpen)}
              urgency={forecast.claimsOpen > 0 ? 'attention' : 'neutral'}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="LD exposure"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.ldExposureAdvisory)}
              urgency={forecast.ldExposureAdvisory > 0 ? 'critical' : 'neutral'}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <ControlTowerMetricTile
              title="Projected final"
              count={0}
              valueDisplay={fmtMoney(currency, forecast.projectedFinal)}
            />
          </Col>
        </ControlTowerMetricStrip>
      ) : (
        <Alert type="warning" showIcon message="Forecast unavailable" style={{ marginBottom: 16 }} />
      )}

      {counts ? (
        <div style={{ marginTop: 16 }}>
          <ControlTowerMetricStrip zone="neutral" title="Work queues">
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile title="Payment apps" count={counts.paymentApplications} />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile title="Certifications" count={counts.certifications} />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Variations"
                count={counts.variations}
                urgency={counts.variations > 0 ? 'attention' : 'neutral'}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile
                title="Claims"
                count={counts.claims}
                urgency={counts.claims > 0 ? 'attention' : 'neutral'}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile title="EOT" count={counts.eotRequests} />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <ControlTowerMetricTile title="Closeouts" count={counts.closeouts} />
            </Col>
          </ControlTowerMetricStrip>
        </div>
      ) : null}

      {towerQ.data?.overdueNotices?.length ? (
        <Paragraph style={{ marginTop: 16 }}>
          <Text type="danger">{towerQ.data.overdueNotices.length} overdue notice(s)</Text>
        </Paragraph>
      ) : null}
    </div>
  );
};

export default EpcContractAdminControlPage;
